import React, { useRef, useCallback } from 'react';
import { gsap } from './gsapClient';
import { useReducedMotion } from './useReducedMotion';

interface TiltProps {
    children: React.ReactNode;
    /** Max rotation in degrees (default 4) */
    max?: number;
    className?: string;
    style?: React.CSSProperties;
}

/**
 * Tilt — subtle 3D perspective tilt following the pointer.
 * The wrapper establishes the perspective; the child rotates.
 * Pointer-driven, so it only ever engages on devices with a fine pointer.
 *
 * Usage:
 *   <Tilt max={5}><ArticleCard ... /></Tilt>
 */
export const Tilt: React.FC<TiltProps> = ({
    children,
    max = 4,
    className,
    style,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    const onMove = useCallback(
        (e: React.PointerEvent) => {
            const el = ref.current;
            if (!el || reduced || e.pointerType !== 'mouse') return;
            const rect = el.getBoundingClientRect();
            const px = (e.clientX - rect.left) / rect.width - 0.5;   // -0.5..0.5
            const py = (e.clientY - rect.top) / rect.height - 0.5;
            gsap.to(el, {
                rotateY: px * max * 2,
                rotateX: -py * max * 2,
                transformPerspective: 900,
                duration: 0.4,
                ease: 'power2.out',
            });
        },
        [max, reduced]
    );

    const onLeave = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        gsap.to(el, {
            rotateX: 0,
            rotateY: 0,
            duration: 0.6,
            ease: 'power3.out',
        });
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            style={{ transformStyle: 'preserve-3d', willChange: 'transform', ...style }}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            {children}
        </div>
    );
};
